import { useEffect, useRef, useState } from 'react';
import { isAutomated, useReducedMotion } from '@ai-tutor/ui';
import type { PlanRow } from './planRows';
import { PlanRowView } from './PlanRowView';

/** One row of the plan appears this often, as if being written. */
export const PLAN_ROW_MS = 350;

/** The top and bottom of the feed fade, so rows slide in and out of view. */
const FADE_EDGES =
  'linear-gradient(to bottom, transparent, black 1.5rem, black calc(100% - 1.5rem), transparent)';

/** How many rows are shown: one more every PLAN_ROW_MS, or all at once
 *  when motion is reduced (or under automation, for stable screenshots). */
function useRevealed(total: number): number {
  const still = useReducedMotion() || isAutomated();
  const [shown, setShown] = useState(still ? total : 0);
  useEffect(() => {
    if (still) {
      setShown(total);
      return;
    }
    if (shown >= total) return;
    const timer = window.setTimeout(() => setShown(n => n + 1), PLAN_ROW_MS);
    return () => window.clearTimeout(timer);
  }, [still, shown, total]);
  return shown;
}

/** Keep the newest row in view while rows arrive - until the student
 *  scrolls the feed themselves, which hands it over to them. */
function useFollow(shown: number) {
  const ref = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || !following.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [shown]);
  const stopFollowing = () => {
    following.current = false;
  };
  return { ref, stopFollowing };
}

/**
 * The plan's sections and concepts, appearing one row at a time and gliding
 * down to follow the newest; the edges fade so nothing ends abruptly. The
 * page never scrolls: the feed fits the card, and a student who wants to
 * look back can scroll the feed itself.
 */
export function PlanFeed({ rows }: { rows: PlanRow[] }) {
  const shown = useRevealed(rows.length);
  const { ref, stopFollowing } = useFollow(shown);
  return (
    <div
      ref={ref}
      onWheel={stopFollowing}
      onTouchMove={stopFollowing}
      className='min-h-0 flex-1 overflow-y-auto px-5 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      style={{ maskImage: FADE_EDGES, WebkitMaskImage: FADE_EDGES }}
    >
      <ol aria-label='Lesson plan' className='space-y-0.5'>
        {rows.slice(0, shown).map(row => (
          <li key={row.key} className='animate-pop-in'>
            <PlanRowView row={row} />
          </li>
        ))}
      </ol>
    </div>
  );
}
